import { useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileDown, ImagePlus, Loader2, RotateCcw } from "lucide-react";

export function TenderDocPanel({ tender, letterheads, editable, onChanged, onManageLetterheads }) {
  const [letterhead, setLetterhead] = useState("bocally");
  const [busy, setBusy] = useState(false);
  const organRef = useRef();

  const uploadOrgan = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/tenders/${tender.id}/organ-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Organ-of-state image attached"); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); if (organRef.current) organRef.current.value = ""; }
  };
  const resetOrgan = async () => { await api.delete(`/tenders/${tender.id}/organ-image`); toast.success("Default coat of arms restored"); onChanged(); };

  const openPdf = async () => {
    try {
      const res = await api.get(`/tenders/${tender.id}/pdf`, { params: { letterhead_id: letterhead }, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not generate the tender document"); }
  };

  return (
    <div className="border border-border rounded-md p-4 space-y-4 bg-secondary/30" data-testid="tender-doc-panel">
      <h4 className="font-display font-semibold text-sm uppercase tracking-wide">Tender Document</h4>
      <div className="flex items-center gap-4">
        <img src={tender.organ_image_path ? `${API}/files/${tender.organ_image_path}` : "/gauteng-coat-of-arms.png"} alt="Organ of state" className="h-14 w-auto bg-white rounded-sm border border-border p-1" data-testid="organ-preview" />
        <div className="text-sm">
          <div className="font-bold text-[#0B2A6F] uppercase">{tender.province || "Gauteng Province"}</div>
          <div className="text-[#E8760C] font-semibold uppercase text-xs">{tender.department || "Treasury"}</div>
          <div className="text-[11px] text-muted-foreground">REPUBLIC OF SOUTH AFRICA</div>
        </div>
        {editable && <div className="ml-auto flex gap-1">
          <input ref={organRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadOrgan(e.target.files?.[0])} data-testid="organ-file-input" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => organRef.current?.click()} data-testid="organ-upload-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ImagePlus className="h-4 w-4 mr-1" /> Different organ</>}</Button>
          {tender.organ_image_path && <Button size="sm" variant="ghost" onClick={resetOrgan} data-testid="organ-reset-button"><RotateCcw className="h-4 w-4" /></Button>}
        </div>}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5 flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase text-muted-foreground">Header & footer (letterhead)</Label>
          <Select value={letterhead} onValueChange={setLetterhead}>
            <SelectTrigger className="h-9 bg-white" data-testid="letterhead-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {letterheads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              <SelectItem value="none">No header / footer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {editable && <Button variant="outline" className="h-9" onClick={onManageLetterheads} data-testid="manage-letterheads-button">Load different…</Button>}
        <Button className="h-9 bg-primary hover:bg-[#C81219] text-white" onClick={openPdf} data-testid="tender-pdf-button"><FileDown className="h-4 w-4 mr-1.5" /> Generate Tender PDF</Button>
      </div>
    </div>
  );
}
